import { useState } from 'react';
import { ClipboardList, Loader2, AlertCircle } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { formatTimestamp } from '@/lib/date';
import { OrgUserWithRole, Task } from '@property/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useUnassignedLandMonitoringTasks } from '@/hooks/use-agent-queries';
import { useBulkAssignTasks } from '@/hooks/use-task-mutations';
import { useIsTableMobile } from '@/hooks/use-is-table-mobile';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import { AssigneeSearchInput } from '@/components/assignee-search-input';
import { DueDateCell } from '@/components/data-table/columns';
import { useTableRefresh } from '@/hooks/use-table-refresh';
import { queryKeys } from '@/lib/query-keys';

const PAGE_SIZE = 10;

export function AdminTaskAssignment() {
  const refresh = useTableRefresh(queryKeys.tasks.all());
  const isMobile = useIsTableMobile();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>('beforeDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState<string | undefined>();

  const {
    data: tasks,
    total,
    isLoading,
    isFetching,
    isError,
  } = useUnassignedLandMonitoringTasks({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
    sortBy,
    sortOrder,
    search,
  });

  const { mutate: bulkAssign, isPending } = useBulkAssignTasks();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedUser, setSelectedUser] = useState<OrgUserWithRole | null>(null);

  function handleSelectionChange(selectedRows: Task[]) {
    setSelectedIds(new Set(selectedRows.map((t) => t.id)));
  }

  function handleAssign() {
    if (!selectedUser || selectedIds.size === 0) return;
    bulkAssign(
      { taskIds: Array.from(selectedIds), agentId: selectedUser.id },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          setSelectedUser(null);
        },
      },
    );
  }

  const columns: ColumnDef<Task>[] = [
    {
      id: 'select',
      enableSorting: false,
      enableResizing: false,
      size: 48,
      meta: { label: 'Select', isMandatory: true },
      header: ({ table }) => (
        <div className="pl-3">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
            aria-label="Select all tasks"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="pl-3" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label={`Select task ${row.original.id}`}
          />
        </div>
      ),
    },
    {
      accessorKey: 'location',
      enableSorting: false,
      meta: { label: 'Address', isPrimary: true, isMandatory: true, searchable: true },
      header: () => <span className="text-xs uppercase tracking-wider font-semibold">Address</span>,
      cell: ({ row }) => {
        const { location } = row.original;
        return (
          <div>
            <div className="text-sm font-medium">{location.line1}</div>
            <div className="text-xs text-muted-foreground">
              {location.city}, {location.state}
            </div>
          </div>
        );
      },
    },
    {
      id: 'createdAt',
      accessorFn: (row: Task) => row.createdAt,
      enableSorting: true,
      meta: { className: 'w-36', label: 'Created', hideOnMobile: true },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => (
        <span className="text-sm font-medium whitespace-nowrap text-muted-foreground">
          {formatTimestamp(row.original.createdAt, { dateStyle: 'medium' })}
        </span>
      ),
    },
    {
      id: 'beforeDate',
      accessorFn: (row: Task) => row.before,
      enableSorting: true,
      meta: { className: 'w-36', label: 'Due Date' },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Due Date" />,
      cell: ({ row }) => (
        <DueDateCell dueDate={row.original.before.toString().split('T')[0]} compact={isMobile} />
      ),
    },
  ];

  const canAssign = selectedIds.size > 0 && selectedUser !== null && !isPending;

  return (
    <div id="unassigned-tasks" className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-6 w-6 text-primary" />
        <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
          Unassigned Tasks
        </h2>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="min-w-0 max-w-sm flex-1">
          <AssigneeSearchInput
            selectedUser={selectedUser}
            onSelect={setSelectedUser}
            onClear={() => setSelectedUser(null)}
          />
        </div>
        <Button disabled={!canAssign} onClick={handleAssign}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {selectedIds.size > 0 && selectedUser
            ? `Assign ${selectedIds.size} task${selectedIds.size > 1 ? 's' : ''} to ${selectedUser.name ?? selectedUser.email}`
            : 'Assign tasks'}
        </Button>
      </div>

      <DataTable
        tableId="admin-task-assignment"
        showColumnToggle
        onRefresh={refresh}
        columns={columns}
        data={tasks}
        isLoading={isLoading}
        isFetching={isFetching}
        pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
        sorting={{
          sortBy,
          sortOrder,
          onSortChange: (by, order) => {
            setSortBy(by);
            setSortOrder(order ?? 'asc');
          },
        }}
        onSearchChange={(q) => {
          setSearch(q || undefined);
          setPage(1);
        }}
        emptyState={
          isError
            ? {
                icon: <AlertCircle className="h-6 w-6 text-muted-foreground" />,
                title: 'Failed to load tasks',
                description: 'There was a problem fetching unassigned tasks. Please try again.',
              }
            : {
                icon: <ClipboardList className="h-6 w-6 text-muted-foreground" />,
                title: 'No unassigned tasks',
              }
        }
        enableRowSelection
        onRowSelectionChange={handleSelectionChange}
        getRowId={(row) => row.id}
      />
    </div>
  );
}
